/**
 * analysis.ts — the measurement surfaces' shared vocabulary (`plan-preproduction`
 * M1 / M2).
 *
 * Two things live here, and neither belongs on a screen:
 *
 *  1. **The n floor, mirrored from the contract.** `MIN_ANALYSIS_N` is what
 *     `contract/analysis.ts` gates both surfaces on, and M1's payload publishes
 *     it as `minimumN` — so the item-analysis screen reads the number off the
 *     response and never touches the constant below. **M2 does not**: the
 *     dashboard aggregate carries `sufficient` and no threshold, so a screen
 *     that wants to say *"30 candidates needed, 12 so far"* has nowhere else to
 *     read it from.
 *
 *     Mirrored rather than imported, for the reason `lib/qc.ts` sets out at
 *     length: the client image copies `server/src/contract` without a
 *     `server/node_modules`, so `zod` cannot resolve from those files and
 *     production client code importing `@contract` breaks the container build.
 *     The guard therefore moves to a test — `src/test/distribution.test.tsx`
 *     imports `MIN_ANALYSIS_N` from `@contract/analysis` and fails the build if
 *     these two numbers stop matching. Duplicating the number is cheap;
 *     duplicating it silently is what the test prevents.
 *
 *  2. **How the three ways a statistic can be missing are told apart.** That
 *     distinction is the whole reason `contract/analysis.ts` models them
 *     separately, and it would be re-implemented per column otherwise.
 */
import type { ItemAnalysisRow, NotMeaningful } from '@/lib/api';

/** `MIN_ANALYSIS_N` in `contract/analysis.ts`. Asserted equal by the test. */
export const MIN_ANALYSIS_N = 30;

/** `DECILE_BINS` in `contract/analysis.ts` — ten bins over the 0–100 scale. */
export const DECILE_BINS = 10;

/**
 * WHY a statistic is not on screen. Three answers, and one blank cell for all
 * three tells a psychometrician nothing.
 *
 * - `not_meaningful` — this item TYPE has no defensible definition for it.
 *   **Permanent**: no amount of extra data will ever fill it. A ranking item has
 *   no central tendency because its response is an ordering of option ids, not
 *   because nobody has sat it yet.
 * - `insufficient` — too few responses. **Fixed by more data**, and the screen
 *   says how many more.
 * - `no_variance` — enough data, defined for the type, and still absent: every
 *   respondent answered alike, so there was nothing to compute from. Reporting
 *   `0` here would read as *"this item does not discriminate"* when the truth is
 *   that it could not be measured.
 * - `present` — there is a number.
 */
export type StatAbsence = 'present' | 'not_meaningful' | 'insufficient' | 'no_variance';

/**
 * Classify one statistic on one row.
 *
 * The order matters and is the contract's own: `notMeaningful` is checked first
 * because it is a property of the item TYPE and outranks the sample size — an
 * under-sampled ranking item still has no central tendency, and telling a reader
 * to collect more data for a number that can never exist is worse than saying
 * nothing.
 */
export function absenceOf(
  row: Pick<ItemAnalysisRow, 'notMeaningful' | 'sufficient'>,
  statistic: NotMeaningful,
  value: unknown,
): StatAbsence {
  if ((row.notMeaningful ?? []).includes(statistic)) return 'not_meaningful';
  if (value !== undefined && value !== null) return 'present';
  if (!row.sufficient) return 'insufficient';
  return 'no_variance';
}
